// /site ページ — Stage Pages-1 (2026-07-09)
// WordPress 固定ページの read-only 一覧を表示する。
// WordPress 接続設定は /settings ページ内に集約(独立ページではない)。
//
// 禁:
// - page 編集 / 作成 / 削除 / publish / unpublish ボタンを作らない
// - AI page 生成 / BlogDraftEditor を混入しない
// - 検索 / 複雑な pagination / bulk action は MVP 外

import { useEffect, useState } from "react";
import { ExternalLink, AlertCircle, RefreshCw } from "lucide-react";

import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Table, type TableColumn } from "../../components/ui/Table";
import { ApiError } from "../../api/client";
import {
  fetchSitePages,
  siteApi,
  type SitePageSummary,
  type SyncMeta,
} from "../../api/site";

// status → 日本語ラベル
function pageStatusLabel(status: string): string {
  switch (status) {
    case "publish":
      return "公開";
    case "draft":
      return "下書き";
    case "private":
      return "非公開";
    case "pending":
      return "承認待ち";
    case "future":
      return "予約済み";
    default:
      return status || "—";
  }
}

// status → Badge 色
function pageStatusColor(
  status: string
): "green" | "gray" | "yellow" | "red" | "blue" {
  if (status === "publish") return "green";
  if (status === "draft") return "gray";
  if (status === "private") return "red";
  if (status === "future") return "blue";
  if (status === "pending") return "yellow";
  return "gray";
}

// JST 表示(yyyy-mm-dd hh:mm)— utils/dateTime と同等の最小実装
function formatJst(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(d).reduce<Record<string, string>>(
      (acc, p) => {
        acc[p.type] = p.value;
        return acc;
      },
      {}
    );
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  } catch {
    return iso;
  }
}

export const SitePage = () => {
  return (
    <div className="grid-12">
      {/* Stage Pages-1:固定ページ一覧(read-only)。
          旧「サイト管理は今後対応予定です」占位 Card は削除 — 本ページは
          固定ページ一覧表示が本来の責務になったため。 */}
      <div className="col-12">
        <SitePagesSection />
      </div>
    </div>
  );
};

// ============================================================
// Stage Pages-1:固定ページ一覧 section
// 4 状態:loading / error / empty / list
// ============================================================

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string; code: string }
  | { kind: "ready"; pages: SitePageSummary[]; syncMeta: SyncMeta | null };

function SitePagesSection() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshStarted, setAutoRefreshStarted] = useState(false);

  async function load() {
    setState({ kind: "loading" });
    try {
      const data = await fetchSitePages();
      setState({
        kind: "ready",
        pages: data.pages ?? [],
        syncMeta: data.sync_meta ?? null,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setState({ kind: "error", message: e.message, code: e.code });
      } else {
        setState({
          kind: "error",
          message: "固定ページの取得に失敗しました。",
          code: "UNKNOWN",
        });
      }
    }
  }

  async function refresh(force = true) {
    setRefreshing(true);
    try {
      await siteApi.refreshPages(force);
      const data = await fetchSitePages();
      setState({
        kind: "ready",
        pages: data.pages ?? [],
        syncMeta: data.sync_meta ?? null,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setState({ kind: "error", message: e.message, code: e.code });
      } else {
        setState({
          kind: "error",
          message: "固定ページの更新に失敗しました。",
          code: "UNKNOWN",
        });
      }
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.kind !== "ready" || autoRefreshStarted) return;
    if (!state.syncMeta?.stale) return;
    setAutoRefreshStarted(true);
    void refresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, autoRefreshStarted]);

  const columns: TableColumn<SitePageSummary>[] = [
    {
      key: "title",
      header: "タイトル",
      render: (row) => <span className="site-pages-title-cell">{row.title || "—"}</span>,
    },
    {
      key: "status",
      header: "ステータス",
      width: 120,
      render: (row) => (
        <Badge color={pageStatusColor(row.status)} dot>
          {pageStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      key: "slug",
      header: "Slug",
      render: (row) =>
        row.slug ? (
          <code className="site-pages-slug">/{row.slug}</code>
        ) : (
          "—"
        ),
    },
    {
      key: "id",
      header: "WordPress ID",
      width: 130,
      render: (row) => <span className="site-pages-id">#{row.wordpress_page_id}</span>,
    },
    {
      key: "modified",
      header: "更新日時",
      width: 170,
      render: (row) => (
        <span className="site-pages-time">{formatJst(row.modified)}</span>
      ),
    },
    {
      key: "actions",
      header: "操作",
      width: 170,
      render: (row) =>
        row.link ? (
          <a
            href={row.link}
            target="_blank"
            rel="noopener noreferrer"
            className="site-pages-link"
          >
            <ExternalLink size={12} />
            <span>WordPressで開く</span>
          </a>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <Card
      title="固定ページ一覧"
      subtitle="接続中の WordPress サイトに登録された固定ページ(read-only)"
      actions={
        <Button
          variant="ghost"
          iconLeft={<RefreshCw size={14} />}
          onClick={() => void refresh(true)}
          disabled={state.kind === "loading" || refreshing}
        >
          {refreshing ? "更新中..." : "今すぐ更新"}
        </Button>
      }
    >
      {state.kind === "loading" && (
        <div className="site-pages-loading">固定ページを読み込み中...</div>
      )}

      {state.kind === "error" && (
        <div className="site-pages-error" role="alert">
          <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          {state.message}
          <span style={{ marginLeft: 8, color: "#9ca3af", fontSize: 11 }}>
            error_code: {state.code}
          </span>
        </div>
      )}

      {state.kind === "ready" && state.pages.length === 0 && (
        <div className="site-pages-empty">
          固定ページが見つかりません。
          <br />
          WordPress 側で固定ページを作成すると、ここに表示されます。
        </div>
      )}

      {state.kind === "ready" && state.pages.length > 0 && (
        <Table<SitePageSummary>
          columns={columns}
          data={state.pages}
          rowKey={(row) => String(row.wordpress_page_id)}
        />
      )}
    </Card>
  );
}
